<template>
    <div class="content-container">
        <div class="filter-container">
            <Filter
                :filter="filter"
                :statusList="statusList"
                class="filter"
                @addFilterStatus="addFilterStatus"
                @clearFilter="clearFilter"
                @removeFilterStatus="removeFilterStatus"
                @search="search"
                @setCreatedDateFrom="setCreatedDateFrom"
                @setCreatedDateTo="setCreatedDateTo"
                @setDemandeInterventionId="setDemandeInterventionId"
                @statusToggleSelectAll="statusToggleSelectAll"
            />
        </div>
        <div class="dataTable">
            <div style="margin-bottom: 8px">
                <h2>{{ count }} {{ $tc('interventionRequest.dataTable.title', count > 1 ? 2 : 1) }}</h2>
                <BorderedIconButton
                    v-if="!isExportDisabled"
                    :imgUrl="iconeXls"
                    :title="$t('list.export')"
                    @click="exportXls"
                />
            </div>
            <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
            <div v-else ref="content" class="datatable-container-smaller" @scroll="handleScroll">
                <DataTable
                    :count="+count"
                    :isLoading="isLoading"
                    :stop-propa-on-pdf="true"
                    :tableData="tableData"
                    :tableHeader="tableHeader"
                    @downloadPdf="downloadPdf"
                    @select="goToPage"
                    @sort="doSort"
                />
            </div>
        </div>
        <WaitingModal v-if="isExportXlsLoading" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import iconeXls from '@/assets/img/xls.svg';
import { store } from '@/plugins/store';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { InterventionRequest } from '@/models/InterventionRequest';
import dayjs from 'dayjs';
import { FilterDemandeIntervention } from '@/modules/DemandeIntervention/services/DemandeInterventionService';
import { SortType, sortValue } from '@/models/SortType';
import { DemandeInterventionActions } from '@/modules/DemandeIntervention/store/action-types';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import router from '@/router';
import DownloadPdf from '@/common/utils/DownloadPdf';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { EtatDemandeIntervention } from '@/models/DemandeIntervention';

export default defineComponent({
    name: 'HistoriqueDemandesInterventionsTab',
    components: { Filter, DataTable, LoaderComponent, BorderedIconButton, WaitingModal },
    props: {
        phaseId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            filter: {
                statusList: [] as string[],
                demandeInterventionId: '',
                createdDateFrom: '',
                createdDateTo: '',
            },
        };
    },
    beforeMount() {
        this.filter.statusList = [...this.filterStatus];
        this.filter.demandeInterventionId = this.filterDemandeInterventionId;
        this.filter.createdDateFrom = this.filterCreatedDateFrom;
        this.filter.createdDateTo = this.filterCreatedDateTo;
    },
    setup() {
        const content = ref<HTMLElement | null>(null);
        return {
            content,
            iconeXls,
        };
    },
    computed: {
        historiqueDemandeInterventionState() {
            return store.state.equipementState.historiqueDemandesInterventions;
        },

        tableData() {
            return store.getters['equipementState/getHistoriqueDemandesInterventions'].map(
                ({ id, libelle, delai, statut, dateCreation, rapportPresent }: InterventionRequest) => {
                    statut = statusName(statut);

                    return {
                        id: { pageName: 'interventionRequestDetailView', value: id, label: id },
                        objet: libelle,
                        date: dayjs(dateCreation).format('DD/MM/YYYY'),
                        delai,
                        statut,
                        pdfExist: rapportPresent,
                    };
                }
            );
        },
        tableHeader() {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsHeaders'];
        },
        count() {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsCount'];
        },
        isLoading() {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsLoading'];
        },
        page() {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsPage'];
        },
        numberOfPages() {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsNbOfPages'];
        },
        filterStatus(): string[] {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsFilter'].statusList;
        },
        filterDemandeInterventionId(): string {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsFilter'].demandeInterventionId;
        },
        filterCreatedDateFrom(): string {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsFilter'].createdDateFrom;
        },
        filterCreatedDateTo(): string {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsFilter'].createdDateTo;
        },
        statusList() {
            return Object.values(EtatDemandeIntervention);
        },
        allStatusSelected() {
            return this.filter.statusList.length === this.statusList.length || this.filter.statusList.length === 0;
        },
        isExportDisabled() {
            return this.tableData.length === 0 || this.isLoading;
        },
        isExportXlsLoading() {
            return store.getters['equipementState/getHistoriqueDemandesInterventionsExportXlsIsLoading'];
        },
    },
    mounted() {
        this.getDemandesInterventionData();
    },

    methods: {
        getDemandesInterventionData(page = 0) {
            const storeFilter = store.getters['equipementState/getHistoriqueDemandesInterventionsFilter'];
            const filters: FilterDemandeIntervention = {
                page,
                etats: this.allStatusSelected ? [] : storeFilter.statusList,
                idDemande: storeFilter.demandeInterventionId !== '' ? storeFilter.demandeInterventionId : undefined,
                dateDebut: storeFilter.createdDateFrom !== '' ? storeFilter.createdDateFrom : undefined,
                dateFin: storeFilter.createdDateTo !== '' ? storeFilter.createdDateTo : undefined,
                phaseIds: [this.phaseId],
                isBim: true,
                sort: this.historiqueDemandeInterventionState.sort
                    ? sortValue(
                          this.historiqueDemandeInterventionState.sort.sortData,
                          this.historiqueDemandeInterventionState.sort.sortStatus
                      )
                    : undefined,
            };

            store.dispatch(
                `suiviDemandeInterventionModuleState/${DemandeInterventionActions.GET_DEMANDES_INTERVENTION}`,
                filters,
                {
                    root: true,
                }
            );
        },
        exportXls() {
            const storeFilter = store.getters['equipementState/getHistoriqueDemandesInterventionsFilter'];

            store.dispatch(`suiviDemandeInterventionModuleState/${DemandeInterventionActions.EXPORT_XLS}`, {
                page: 0,
                etats: this.allStatusSelected ? [] : storeFilter.statusList,
                idDemande: storeFilter.demandeInterventionId !== '' ? storeFilter.demandeInterventionId : undefined,
                dateDebut: storeFilter.createdDateFrom !== '' ? storeFilter.createdDateFrom : undefined,
                dateFin: storeFilter.createdDateTo !== '' ? storeFilter.createdDateTo : undefined,
                phaseIds: [this.phaseId],
                isBim: true,
                sort: this.historiqueDemandeInterventionState.sort
                    ? sortValue(
                          this.historiqueDemandeInterventionState.sort.sortData,
                          this.historiqueDemandeInterventionState.sort.sortStatus
                      )
                    : undefined,
            });
        },
        search() {
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_FILTER_DEMANDE_INTERVENTION_ID}`,
                this.filter.demandeInterventionId
            );
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_FILTER_STATUS}`,
                this.filter.statusList
            );
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_FILTER_CREATED_DATE_FROM}`,
                this.filter.createdDateFrom
            );
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_FILTER_CREATED_DATE_TO}`,
                this.filter.createdDateTo
            );

            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_ITEMS}`, []);
            this.getDemandesInterventionData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_SORT}`, sort);
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_ITEMS}`, []);
            this.getDemandesInterventionData();
        },
        setDemandeInterventionId(value: string) {
            this.filter.demandeInterventionId = value;
        },
        setCreatedDateFrom(value: string) {
            this.filter.createdDateFrom = value;
        },
        setCreatedDateTo(value: string) {
            this.filter.createdDateTo = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(s => s !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList =
                this.filter.statusList.length === this.statusList.length ? [] : [...this.statusList];
        },
        clearFilter() {
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_RESET_FILTER}`);
            this.filter.demandeInterventionId = '';
            this.filter.createdDateFrom = '';
            this.filter.createdDateTo = '';
            this.filter.statusList = [...this.statusList];

            this.search();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getDemandesInterventionData(this.page + 1);
            }
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        downloadPdf(item: { id: { pageName: string; value: number } }) {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention-');
        },
    },
});
</script>

<style scoped>
.dataTable > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
